/* eslint-disable react-hooks/refs -- @dnd-kit node refs and isOver are used during render by design */
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  PermissionCode,
  UNASSIGNED_QUEUE_COLUMN,
  type QueueBoardColumn,
  type TicketListItem,
} from '@gb-ticket/shared-types';
import { GripVerticalIcon, Loader2Icon, MoreHorizontalIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import type { QueueFormValues } from '@/products/helpdesk/features/queues/queue-form-dialog';
import { PriorityBadge, StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import {
  useAssignTicketQueue,
  useQueueBoard,
  useReorderQueues,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PersonChip } from '@/platform/components/person-chip';

type ColumnDragData = { type: 'column'; columnId: string };
type TicketDragData = { type: 'ticket'; ticketId: string; columnId: string };

type QueueBoardProps = {
  canManage?: boolean;
  onEditQueue?: (values: QueueFormValues) => void;
  onDeactivateQueue?: (values: { id: string; name: string; openCount: number }) => void;
};

export function QueueBoard({ canManage = false, onEditQueue, onDeactivateQueue }: QueueBoardProps) {
  const me = useMe();
  const canAssign = hasPermission(me.data, PermissionCode.TICKETS_ASSIGN);
  const board = useQueueBoard();
  const reorder = useReorderQueues();
  const assign = useAssignTicketQueue();
  const [columnOrder, setColumnOrder] = useState<string[] | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeTicket, setActiveTicket] = useState<TicketListItem | null>(null);

  const columns = useMemo(() => {
    const server = board.data?.columns ?? [];
    if (!columnOrder) {
      return server;
    }
    const byId = new Map(server.map((column) => [column.id, column]));
    const unassigned = server.filter((column) => !column.queue);
    const orderedQueues = columnOrder
      .map((id) => byId.get(id))
      .filter((column): column is QueueBoardColumn => Boolean(column?.queue));
    return [...unassigned, ...orderedQueues];
  }, [board.data, columnOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const queueColumnIds = useMemo(
    () => columns.filter((column) => column.queue).map((column) => columnSortableId(column.id)),
    [columns],
  );
  const activeColumn = columns.find(
    (column) => column.id === activeId || columnSortableId(column.id) === activeId,
  );

  const collisionDetection: CollisionDetection = (args) => {
    const activeData = asDragData(args.active.data.current);
    if (activeData?.type === 'column') {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) => {
          const data = asDragData(container.data.current);
          return data?.type === 'column' && container.id !== UNASSIGNED_QUEUE_COLUMN;
        }),
      });
    }
    const pointerHits = pointerWithin(args);
    return pointerHits.length > 0 ? pointerHits : closestCenter(args);
  };

  function onDragStart(event: DragStartEvent) {
    setActiveId(event.active.id);
    const data = asDragData(event.active.data.current);
    if (data?.type === 'ticket') {
      const ticket = columns
        .flatMap((column) => column.tickets)
        .find((item) => item.id === data.ticketId);
      setActiveTicket(ticket ?? null);
      return;
    }
    setActiveTicket(null);
  }

  async function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);
    setActiveTicket(null);
    if (!over || !canAssign) {
      return;
    }
    const activeData = asDragData(active.data.current);
    if (!activeData) {
      return;
    }

    if (activeData.type === 'column') {
      const overData = asDragData(over.data.current);
      const overColumnId = overData?.type === 'column' ? overData.columnId : null;
      if (!overColumnId || overColumnId === UNASSIGNED_QUEUE_COLUMN) {
        return;
      }
      const queueIds = columns.filter((column) => column.queue).map((column) => column.id);
      const oldIndex = queueIds.indexOf(activeData.columnId);
      const newIndex = queueIds.indexOf(overColumnId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
        return;
      }
      const nextIds = arrayMove(queueIds, oldIndex, newIndex);
      setColumnOrder(nextIds);
      try {
        await reorder.mutateAsync({ queueIds: nextIds });
        setColumnOrder(null);
        toast.success('Reihenfolge gespeichert.');
      } catch (error) {
        setColumnOrder(null);
        toast.error(
          error instanceof ApiRequestError
            ? error.message
            : 'Die Reihenfolge konnte nicht gespeichert werden.',
        );
        await board.refetch();
      }
      return;
    }

    const destinationId = destinationColumnId(over.data.current, over.id);
    if (!destinationId || destinationId === activeData.columnId) {
      return;
    }
    try {
      await assign.mutateAsync({
        ticketId: activeData.ticketId,
        queueId: destinationId === UNASSIGNED_QUEUE_COLUMN ? null : destinationId,
      });
      toast.success('Ticket verschoben.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Die Zuweisung ist fehlgeschlagen.',
      );
    }
  }

  if (board.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Warteschlangen werden geladen …
      </p>
    );
  }

  if (board.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {board.error instanceof ApiRequestError
          ? board.error.message
          : 'Das Queue-Board konnte nicht geladen werden.'}
      </p>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={activeColumn && !activeTicket ? [restrictToHorizontalAxis] : undefined}
      onDragStart={onDragStart}
      onDragEnd={(event) => void onDragEnd(event)}
      onDragCancel={() => {
        setActiveId(null);
        setActiveTicket(null);
      }}
    >
      <SortableContext items={queueColumnIds} strategy={horizontalListSortingStrategy}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map((column) => (
            <QueueColumn
              key={column.id}
              column={column}
              canAssign={canAssign}
              canManage={canManage}
              busy={reorder.isPending || assign.isPending}
              onEdit={
                column.queue && onEditQueue
                  ? () =>
                      onEditQueue({
                        id: column.queue!.id,
                        name: column.queue!.name,
                        slug: column.queue!.slug,
                        teamId: column.queue!.teamId,
                        defaultAssigneeId: column.queue!.defaultAssigneeId,
                        isActive: column.queue!.isActive,
                      })
                  : undefined
              }
              onDeactivate={
                column.queue && onDeactivateQueue
                  ? () =>
                      onDeactivateQueue({
                        id: column.queue!.id,
                        name: column.queue!.name,
                        openCount: column.openCount,
                      })
                  : undefined
              }
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeTicket ? <TicketCard ticket={activeTicket} overlay /> : null}
        {activeColumn && !activeTicket ? (
          <div className="w-72 rounded-xl border bg-card p-3 shadow-lg">
            <p className="font-medium">{activeColumn.queue?.name ?? 'Nicht zugewiesen'}</p>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function QueueColumn({
  column,
  canAssign,
  canManage,
  busy,
  onEdit,
  onDeactivate,
}: {
  column: QueueBoardColumn;
  canAssign: boolean;
  canManage: boolean;
  busy: boolean;
  onEdit?: () => void;
  onDeactivate?: () => void;
}) {
  const sortable = useSortable({
    id: column.queue ? columnSortableId(column.id) : `locked:${column.id}`,
    data: { type: 'column', columnId: column.id } satisfies ColumnDragData,
    disabled: !column.queue || !canAssign || busy,
  });
  const droppable = useDroppable({
    id: column.id,
    data: { type: 'column', columnId: column.id } satisfies ColumnDragData,
    disabled: !canAssign || busy,
  });

  const style = column.queue
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }
    : undefined;

  const title = column.queue?.name ?? 'Nicht zugewiesen';
  const remaining =
    column.openCount > column.tickets.length
      ? ` · ${column.tickets.length} von ${column.openCount}`
      : '';
  const showMenu = Boolean(column.queue && canManage && (onEdit ?? onDeactivate));

  return (
    <Card
      ref={column.queue ? sortable.setNodeRef : undefined}
      style={style}
      className={cn(
        'w-72 shrink-0',
        sortable.isDragging && 'opacity-60',
        droppable.isOver && 'ring-2 ring-ring',
      )}
    >
      <CardHeader className="gap-2 px-4">
        <div className="flex items-start gap-2">
          {column.queue && canAssign && (
            <button
              type="button"
              className="mt-0.5 cursor-grab touch-none text-muted-foreground hover:text-foreground"
              aria-label={`${title} verschieben`}
              {...sortable.attributes}
              {...sortable.listeners}
            >
              <GripVerticalIcon className="size-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {column.openCount} offen{remaining}
            </p>
            {(column.healthHints ?? []).length > 0 ? (
              <ul className="mt-1 space-y-0.5 text-xs text-amber-800 dark:text-amber-200">
                {(column.healthHints ?? []).map((hint) => (
                  <li key={hint}>{hint}</li>
                ))}
              </ul>
            ) : null}
          </div>
          {showMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  aria-label={`${title}: Aktionen`}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onEdit();
                    }}
                  >
                    Bearbeiten
                  </DropdownMenuItem>
                )}
                {onDeactivate && (
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => {
                      onDeactivate();
                    }}
                  >
                    Deaktivieren
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent ref={droppable.setNodeRef} className="min-h-40 space-y-2 px-4">
        {column.tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine offenen Tickets.</p>
        ) : (
          column.tickets.map((ticket) => (
            <DraggableTicketCard
              key={ticket.id}
              ticket={ticket}
              columnId={column.id}
              disabled={!canAssign || busy}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function DraggableTicketCard({
  ticket,
  columnId,
  disabled,
}: {
  ticket: TicketListItem;
  columnId: string;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: ticket.id,
    data: { type: 'ticket', ticketId: ticket.id, columnId } satisfies TicketDragData,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn(isDragging && 'opacity-40')}
      {...attributes}
      {...listeners}
    >
      <TicketCard ticket={ticket} />
    </div>
  );
}

function TicketCard({ ticket, overlay = false }: { ticket: TicketListItem; overlay?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-md border bg-background p-3 shadow-xs',
        overlay && 'w-72 cursor-grabbing shadow-lg',
      )}
    >
      <div className="mb-2 flex flex-wrap gap-1">
        <Badge variant="outline">#{ticket.number}</Badge>
        <StatusBadge status={ticket.status} />
        <PriorityBadge priority={ticket.priority} />
      </div>
      {overlay ? (
        <p className="text-sm font-medium">{ticket.subject}</p>
      ) : (
        <Link to={`/helpdesk/tickets/${ticket.number}`} className="hover:underline">
          <p className="text-sm font-medium">{ticket.subject}</p>
        </Link>
      )}
      <p className="mt-1 flex min-w-0 flex-wrap items-center gap-1 truncate text-xs text-muted-foreground">
        <span>Von</span>
        <PersonChip
          displayName={ticket.requester.displayName}
          profilePhotoUrl={ticket.requester.profilePhotoUrl}
          avatarClassName="size-5"
        />
        {ticket.assignee ? (
          <>
            <span>·</span>
            <PersonChip
              displayName={ticket.assignee.displayName}
              profilePhotoUrl={ticket.assignee.profilePhotoUrl}
              avatarClassName="size-5"
            />
          </>
        ) : null}
      </p>
    </div>
  );
}

function columnSortableId(columnId: string): string {
  return `column:${columnId}`;
}

function asDragData(value: unknown): ColumnDragData | TicketDragData | undefined {
  if (!value || typeof value !== 'object' || !('type' in value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (record.type === 'column' && typeof record.columnId === 'string') {
    return { type: 'column', columnId: record.columnId };
  }
  if (
    record.type === 'ticket' &&
    typeof record.ticketId === 'string' &&
    typeof record.columnId === 'string'
  ) {
    return { type: 'ticket', ticketId: record.ticketId, columnId: record.columnId };
  }
  return undefined;
}

function destinationColumnId(data: unknown, overId: UniqueIdentifier): string | null {
  const parsed = asDragData(data);
  if (parsed) {
    return parsed.columnId;
  }
  const id = String(overId);
  if (id.startsWith('column:')) {
    return id.slice('column:'.length);
  }
  if (id.startsWith('locked:')) {
    return id.slice('locked:'.length);
  }
  return id || null;
}
